import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SeasonDateRange, SupportedLanguage } from '../../types';

interface SeasonDateRangeInputProps {
  range: SeasonDateRange;
  onChange: (range: SeasonDateRange) => void;
  onDelete: () => void;
  language: SupportedLanguage;
}

const MONTHS = [
  { value: 1, de: 'Januar', en: 'January' },
  { value: 2, de: 'Februar', en: 'February' },
  { value: 3, de: 'März', en: 'March' },
  { value: 4, de: 'April', en: 'April' },
  { value: 5, de: 'Mai', en: 'May' },
  { value: 6, de: 'Juni', en: 'June' },
  { value: 7, de: 'Juli', en: 'July' },
  { value: 8, de: 'August', en: 'August' },
  { value: 9, de: 'September', en: 'September' },
  { value: 10, de: 'Oktober', en: 'October' },
  { value: 11, de: 'November', en: 'November' },
  { value: 12, de: 'Dezember', en: 'December' },
];

const getDaysInMonth = (month: number): number => {
  // Using a non-leap year for simplicity
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return daysPerMonth[month - 1];
};

const SeasonDateRangeInput: React.FC<SeasonDateRangeInputProps> = ({
  range,
  onChange,
  onDelete,
  language,
}) => {
  const { t } = useTranslation();

  const updateField = (field: keyof SeasonDateRange, value: number | string) => {
    if (field === 'startMonth' || field === 'endMonth') {
      const newMonth = value as number;
      const dayField = field === 'startMonth' ? 'startDay' : 'endDay';
      const currentDay = field === 'startMonth' ? range.startDay : range.endDay;
      const maxDays = getDaysInMonth(newMonth);

      onChange({
        ...range,
        [field]: newMonth,
        [dayField]: Math.min(currentDay, maxDays),
      });
    } else {
      onChange({ ...range, [field]: value });
    }
  };

  const startDays = getDaysInMonth(range.startMonth);
  const endDays = getDaysInMonth(range.endMonth);

  return (
    <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {t('pricing.seasonRange')}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Start Date */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs">{t('pricing.startDate')}</Label>
          <div className="flex gap-2">
            <Select
              value={range.startMonth.toString()}
              onValueChange={(v) => updateField('startMonth', parseInt(v))}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {m[language]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={range.startDay.toString()}
              onValueChange={(v) => updateField('startDay', parseInt(v))}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: startDays }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={day.toString()}>
                    {day}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <Label className="text-xs">{t('pricing.endDate')}</Label>
          <div className="flex gap-2">
            <Select
              value={range.endMonth.toString()}
              onValueChange={(v) => updateField('endMonth', parseInt(v))}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {m[language]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={range.endDay.toString()}
              onValueChange={(v) => updateField('endDay', parseInt(v))}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: endDays }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={day.toString()}>
                    {day}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SeasonDateRangeInput;
